/**
 * Brick — the reward / bump brick (Brief 009).
 *
 * A SOLID, tile-aligned 1×1 entity. A head-bonk:
 *   1. If it hides a reward (`props.reward`) and is not yet spent → pops the reward
 *      and goes inert (acts like a `?`-block first).
 *   2. Else → a bump-nudge.
 *
 * NOTE (2026-07-01): the old "player is BIG → the brick breaks" rule is gone with the
 * SMALL/BIG form toggle (there is one player avatar now). A brick that should be
 * destructible in a combat level is a CRACKEDWALL (attack-breakable), not this. The
 * `props.breakable` flag is accepted-but-inert for back-compat with saved levels.
 *
 * Determinism: floor/min arithmetic + a dt-driven debris timer only → ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { resolveRewardToken } from './rewards';

/** Seconds the bump-nudge plays (render-only). */
const BUMP_SECONDS = 0.18;
/** Peak upward nudge of a bump (px, render-only). */
const BUMP_RISE = 8;
/** Seconds the broken brick lingers before vanishing. */
const DEBRIS_SECONDS = 0.18;

export class Brick implements Entity {
  readonly type = EntityType.BRICK;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private reward?: string;
  private rewardCount: number;
  private spent = false;
  private bumpTimer = 0;
  private debrisTimer = 0;

  /**
   * @param props `reward` (optional — hides a pickup), `count` (default 1).
   *              `breakable` is accepted-but-inert (back-compat; see the class note).
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    this.reward = typeof props?.reward === 'string' ? props.reward : undefined;
    const c = props?.count;
    this.rewardCount = typeof c === 'number' && c > 0 ? Math.floor(c) : 1;
  }

  /** Solid until broken; a broken brick returns null and stops colliding. */
  solidBody(): AABB | null { return this.alive ? this.body : null; }

  update(dt: number, _ctx: EntityContext): void {
    if (this.bumpTimer > 0) this.bumpTimer = Math.max(0, this.bumpTimer - dt);
    if (!this.alive) this.debrisTimer = Math.max(0, this.debrisTimer - dt);
  }

  onBonk(ctx: EntityContext): void {
    // 1. Reward bricks act like a `?`-block until spent.
    if (this.reward && !this.spent) {
      this.spent = true;
      this.bumpTimer = BUMP_SECONDS;
      const { type, props } = resolveRewardToken(this.reward);
      const cx = this.body.x + this.body.w / 2;
      for (let i = 0; i < this.rewardCount; i++) ctx.spawnEntityAt(type, cx + i * 10, this.body.y, props);
      ctx.playSound(this.reward === 'COIN' ? 'coin' : 'bump', 0.5);
      return;
    }
    // 2. Otherwise just bump.
    this.bumpTimer = BUMP_SECONDS;
    ctx.playSound('bump', 0.3);
  }

  /** Kill hook (ctx.killEntity) — open the debris-show window. */
  onKilled(): void {
    if (!this.alive && this.debrisTimer > 0) return;
    this.alive = false;
    this.debrisTimer = DEBRIS_SECONDS;
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — acts via onBonk */ }

  spriteKey(): string {
    if (this.spent) return 'block_used';
    return 'brick';
  }

  visible(): boolean { return this.alive || this.debrisTimer > 0; }

  renderYOffset(): number {
    if (this.bumpTimer <= 0) return 0;
    return -BUMP_RISE * (this.bumpTimer / BUMP_SECONDS);
  }
}
